import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const { processGoogleSession } = useAuth();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // CRITICAL: Use ref to prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        // Extract session_id from URL hash
        const hash = location.hash;
        const sessionIdMatch = hash.match(/session_id=([^&]+)/);
        
        if (!sessionIdMatch) {
          toast.error('Invalid authentication callback');
          navigate('/');
          return;
        }

        const sessionId = sessionIdMatch[1];
        
        // Exchange session_id for user data
        const userData = await processGoogleSession(sessionId);
        
        // Check for pending guest automaton to import
        const pendingData = localStorage.getItem('pendingGuestAutomaton');
        if (pendingData) {
          try {
            const automatonData = JSON.parse(pendingData);
            const { api: apiUtil } = await import('../utils/api');
            const { ok, data: imported } = await apiUtil.post('/automata/import', automatonData);
            if (ok) {
              localStorage.setItem('pendingSelectAutomaton', imported.automaton_id);
            }
          } catch (err) {
            console.error('Failed to import guest automaton:', err);
          } finally {
            localStorage.removeItem('pendingGuestAutomaton');
          }
        }

        toast.success(`Welcome, ${userData.name}!`);
        
        // Clear the hash and navigate to dashboard
        window.history.replaceState(null, '', '/dashboard');
        navigate('/dashboard', { replace: true, state: { user: userData } });
      } catch (error) {
        console.error('Auth callback error:', error);
        toast.error(error.message || 'Authentication failed. Please try again.');
        navigate('/');
      }
    };

    processAuth();
  }, [location.hash, navigate, processGoogleSession]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="text-center space-y-4">
        <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />
        <p className="text-muted-foreground">Completing sign in...</p>
      </div>
    </div>
  );
}
